import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Button, Checkbox, Loader, Segment, Table } from "semantic-ui-react";
import { GetNotifySubscriberContactMethodsForLoggedInUserResponse } from "../../../../../../../queries/notify";
import { buildReturnUrl } from "../../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTPInURL";
import { useAuthOTPSend } from "../../../../../../auth/hooks/useAuthOTPSend";
import { useNotifySubscriberContactMethodUpsert } from "../../../../../hooks/useNotifySubscriberContactMethodCreate";
import { useNotifySubscriberContactMethodOptIn } from "../../../../../hooks/useNotifySubscriberContactMethodOptIn";
import { useNotifySubscriberContactMethodOptOut } from "../../../../../hooks/useNotifySubscriberContactMethodOptOut";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { buildIdentifierString } from "../../../subscribers/utils/buildIdentifierString";
import { useSubscriberContactMethodPlaceholderFlags } from "./hooks/useSubscriberContactMethodPlaceholderFlags";
import { AddSubscriberContactMethodModal } from "./modals/AddSubscriberContactMethodModal";
import { DeleteSubscriberContactMethodModal } from "./modals/DeleteSubscriberContactMethodModal";
import { OTPModalData, VerifyOTPModal } from "./modals/VerifyOTPModal";
import { buildAppIdentifierString } from "../../../subscribers/utils/buildAppIdentifierString";

type SubscriberContactMethod = GetNotifySubscriberContactMethodsForLoggedInUserResponse[number];

export function SubscriberContactMethodTable() {
  const [isAddContactMethodModalOpen, setIsAddContactMethodModalOpen] = useState(false);
  const [otpModalData, setOTPModalData] = useState<OTPModalData | null>(null);
  const [deleteModalData, setDeleteModalData] = useState<{
    subscriberContactMethodId: number;
  } | null>(null);

  const { subscriberContactMethods } = useNotifySubscriberContactMethodsForLoggedInUser();

  const {
    isUserEmailVerified,
    isUserPhoneVerified,
    userEmail,
    userPhone,
    shouldShowEmailPlaceholder,
    shouldShowPhonePlaceholder,
  } = useSubscriberContactMethodPlaceholderFlags();

  const { sendAuthOTP, isAuthOTPSendLoading } = useAuthOTPSend({
    onSuccess: ({ identifier, type }) => {
      setIsAddContactMethodModalOpen(false);
      setOTPModalData({
        identifier,
        type,
      });
    },
  });

  const { upsertSubscriberContactMethod, isUpsertSubscriberContactMethodLoading } =
    useNotifySubscriberContactMethodUpsert({
      onSuccess: () => {
        setOTPModalData(null);
      },
    });

  const resendVerification = (method: SubscriberContactMethod) => {
    if (method.type === "App") return;
    sendAuthOTP({
      identifier: method.identifier,
      type: method.type,
      returnUrl: buildReturnUrl(method.type, method.identifier),
    });
    setOTPModalData({
      identifier: method.identifier,
      type: method.type,
    });
  };

  const enrollUserIdentifier = async (identifier: string, type: "Email" | "Phone") => {
    const identifierIsVerified = type === "Email" ? isUserEmailVerified : isUserPhoneVerified;
    // If the identifier is already verified, we don't need to send an OTP
    // and can directly upsert the contact method
    if (identifierIsVerified) {
      await upsertSubscriberContactMethod({
        identifier,
        identifierOrigin: "WebPortal",
        identifierType: type,
      });
      return;
    }

    await sendAuthOTP({
      identifier,
      type,
      returnUrl: buildReturnUrl(type, identifier),
    });
    setOTPModalData({
      identifier,
      type,
    });
  };

  return (
    <>
      <DeleteSubscriberContactMethodModal data={deleteModalData} onClose={() => setDeleteModalData(null)} />
      <AddSubscriberContactMethodModal
        open={isAddContactMethodModalOpen}
        onClose={() => setIsAddContactMethodModalOpen(false)}
        onSubmit={async ({ identifier, type }) => {
          await sendAuthOTP({
            identifier,
            type,
            returnUrl: buildReturnUrl(type, identifier),
          });
        }}
        isLoading={isAuthOTPSendLoading}
        submitTitle='Add'
        showSignInLink={false}
      />
      <VerifyOTPModal
        data={otpModalData}
        onClose={() => setOTPModalData(null)}
        onChangeIdentifier={() => {
          setOTPModalData(null);
          setIsAddContactMethodModalOpen(true);
        }}
        verifyOTPMutation={{
          isLoading: isUpsertSubscriberContactMethodLoading,
          mutate: async ({ identifier, type, otp }) => {
            await upsertSubscriberContactMethod({
              identifier,
              identifierOrigin: "WebPortal",
              identifierType: type,
              otp,
            });
          },
        }}
      />
      {/* Display table when screen is medium or larger */}
      <div className='tw-hidden tw-overflow-x-auto md:tw-block'>
        <Table unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Method</Table.HeaderCell>
              <Table.HeaderCell collapsing></Table.HeaderCell>
              <Table.HeaderCell collapsing className='tw-w-28'>
                Enabled
              </Table.HeaderCell>
              <Table.HeaderCell collapsing>
                <span className='tw-sr-only'>Actions</span>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {shouldShowEmailPlaceholder && (
              <Table.Row>
                <Table.Cell>{buildIdentifierString({ identifier: userEmail, type: "Email" })} (primary)</Table.Cell>
                <Table.Cell colSpan={3}>
                  <Button
                    className='button-ghost'
                    onClick={() => enrollUserIdentifier(userEmail, "Email")}
                    loading={isUpsertSubscriberContactMethodLoading}
                    disabled={isUpsertSubscriberContactMethodLoading}
                  >
                    Enroll
                  </Button>
                </Table.Cell>
              </Table.Row>
            )}
            {shouldShowPhonePlaceholder && (
              <Table.Row>
                <Table.Cell>{buildIdentifierString({ identifier: userPhone, type: "Phone" })} (primary)</Table.Cell>
                <Table.Cell colSpan={3}>
                  <Button
                    className='button-ghost'
                    onClick={() => enrollUserIdentifier(userPhone, "Phone")}
                    loading={isUpsertSubscriberContactMethodLoading}
                    disabled={isUpsertSubscriberContactMethodLoading}
                  >
                    Enroll
                  </Button>
                </Table.Cell>
              </Table.Row>
            )}
            {subscriberContactMethods?.map((method) => (
              <Table.Row key={method.id}>
                <Table.Cell>
                  {method.type == "App"
                    ? buildAppIdentifierString(method.metadata.platformDetails, method.metadata.deviceType)
                    : buildIdentifierString(method)}{" "}
                  {method.primary && "(primary)"}
                </Table.Cell>
                <Table.Cell collapsing>
                  {!method.verifiedAt && (
                    <Button
                      className='button-ghost'
                      onClick={() => resendVerification(method)}
                      disabled={isAuthOTPSendLoading}
                    >
                      Re-Send Verification
                    </Button>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {method.verifiedAt ? (
                    <EnabledCheckbox subscriberContactMethod={method} />
                  ) : (
                    <p className='tw-whitespace-nowrap tw-italic'>Must verify first</p>
                  )}
                </Table.Cell>
                <Table.Cell collapsing>
                  {!method.primary && (
                    <Button
                      className='button-ghost gray tw-p-3'
                      onClick={() => setDeleteModalData({ subscriberContactMethodId: method.id })}
                      aria-label='Remove'
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row className='tw-h-[58px]'>
              <Table.Cell colSpan={4}>
                <Button className='no-padding-button' onClick={() => setIsAddContactMethodModalOpen(true)}>
                  Add Contact Method
                </Button>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
      {/* Display cards when screen is smaller than medium */}
      <div className='tw-space-y-2 md:tw-hidden'>
        {shouldShowEmailPlaceholder && (
          <Segment className='tw-m-0 tw-flex tw-items-center tw-justify-between tw-overflow-hidden'>
            <span>{buildIdentifierString({ identifier: userEmail, type: "Email" })} (primary)</span>
            <Button
              className='button-ghost'
              onClick={() => enrollUserIdentifier(userEmail, "Email")}
              loading={isUpsertSubscriberContactMethodLoading}
              disabled={isUpsertSubscriberContactMethodLoading}
            >
              Enroll
            </Button>
          </Segment>
        )}
        {shouldShowPhonePlaceholder && (
          <Segment className='tw-m-0 tw-flex tw-items-center tw-justify-between tw-overflow-hidden'>
            <span>{buildIdentifierString({ identifier: userPhone, type: "Phone" })} (primary)</span>
            <Button
              className='button-ghost'
              onClick={() => enrollUserIdentifier(userPhone, "Phone")}
              loading={isUpsertSubscriberContactMethodLoading}
              disabled={isUpsertSubscriberContactMethodLoading}
            >
              Enroll
            </Button>
          </Segment>
        )}
        {subscriberContactMethods?.map((method) => {
          if (!method.verifiedAt) {
            return (
              <Segment key={method.id} className='tw-m-0 tw-flex tw-flex-col tw-gap-3'>
                <span className='tw-font-bold'>
                  {method.type == "App"
                    ? buildAppIdentifierString(method.metadata.platformDetails, method.metadata.deviceType)
                    : buildIdentifierString(method)}
                </span>
                <div>
                  <Button
                    className='no-padding-button'
                    onClick={() => resendVerification(method)}
                    disabled={isAuthOTPSendLoading}
                  >
                    Re-Send Verification
                  </Button>
                </div>
              </Segment>
            );
          }

          return (
            <Segment key={method.id} className='tw-m-0 tw-flex tw-min-h-[65px] tw-justify-between tw-overflow-hidden'>
              <EnabledCheckbox subscriberContactMethod={method} showLabel />
              {!method.primary && (
                <Button
                  className='button-ghost gray tw-p-3'
                  onClick={() => setDeleteModalData({ subscriberContactMethodId: method.id })}
                  aria-label='Remove'
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              )}
            </Segment>
          );
        })}
        <Segment className='tw-m-0'>
          <Button className='no-padding-button' onClick={() => setIsAddContactMethodModalOpen(true)}>
            Add Contact Method
          </Button>
        </Segment>
      </div>
    </>
  );
}

function EnabledCheckbox({
  subscriberContactMethod,
  showLabel = false,
}: {
  subscriberContactMethod: SubscriberContactMethod;
  showLabel?: boolean;
}) {
  const { DANGER_optInSubscriberContactMethod, isOptInSubscriberContactMethodLoading } =
    useNotifySubscriberContactMethodOptIn();
  const { optOutSubscriberContactMethod, isOptOutSubscriberContactMethodLoading } =
    useNotifySubscriberContactMethodOptOut();

  const isUpdating = isOptInSubscriberContactMethodLoading || isOptOutSubscriberContactMethodLoading;

  const toggleEnableContactMethod = async () => {
    if (subscriberContactMethod.status === "Enrolled") {
      await optOutSubscriberContactMethod({
        subscriberContactMethodIds: [subscriberContactMethod.id],
      });
    } else {
      await DANGER_optInSubscriberContactMethod({
        subscriberContactMethodIds: [subscriberContactMethod.id],
        justification: "User opted in via web portal",
      });
    }
  };

  return (
    <div className='tw-flex tw-items-center tw-gap-3'>
      <Checkbox
        toggle
        checked={subscriberContactMethod.status === "Enrolled"}
        onChange={() => toggleEnableContactMethod()}
        disabled={isUpdating}
        label={
          showLabel
            ? subscriberContactMethod.type == "App"
              ? buildAppIdentifierString(
                  subscriberContactMethod.metadata.platformDetails,
                  subscriberContactMethod.metadata.deviceType,
                )
              : buildIdentifierString(subscriberContactMethod)
            : undefined
        }
      />
      <Loader active={isUpdating} size='tiny' inline />
    </div>
  );
}
